/** Continuidad y planta (manuales SISS Continuidad Agua, Continuidad
 * Alcantarillado, Presiones + control diario de desinfección).
 *
 * Cuatro pestañas; la activa vive en la URL (`?tab=agua|alcantarillado|
 * presiones|plantas`) para que F5, atrás y los enlaces del menú y del Inicio
 * caigan en la misma pestaña (NAV-09). El menú lateral enlaza cada pestaña.
 */
import { useSearchParams } from "react-router-dom";
import { Activity, Droplets, FlaskConical, Gauge as GaugeIcon, Waves } from "lucide-react";
import { twMerge } from "tailwind-merge";

import { PageHeader } from "../../components/ui";
import { InterrupcionesPanel } from "./InterrupcionesPanel";
import { ObstruccionesPanel } from "./ObstruccionesPanel";
import { PlantasPanel } from "./PlantasPanel";
import { PresionesPanel } from "./PresionesPanel";

type Tab = "agua" | "alcantarillado" | "presiones" | "plantas";

const TABS: { id: Tab; label: string; icon: typeof Droplets }[] = [
  { id: "agua", label: "Agua potable", icon: Droplets },
  { id: "alcantarillado", label: "Alcantarillado", icon: Waves },
  { id: "presiones", label: "Presiones", icon: GaugeIcon },
  { id: "plantas", label: "Planta y desinfección", icon: FlaskConical },
];

function esTab(v: string | null): v is Tab {
  return TABS.some((t) => t.id === v);
}

export function ContinuidadPage(): JSX.Element {
  // La pestaña sale de la URL en cada render: cambiar la URL (menú, atrás,
  // un link del Inicio) cambia la pestaña, y elegir una pestaña cambia la URL.
  const [params, setParams] = useSearchParams();
  const crudo = params.get("tab");
  const tab: Tab = esTab(crudo) ? crudo : "agua";
  const elegir = (id: Tab): void => {
    const next = new URLSearchParams(params);
    next.set("tab", id);
    setParams(next, { replace: true });
  };

  return (
    <div className="space-y-4">
      <PageHeader
        title="Continuidad y planta"
        icon={Activity}
        subtitle="Registros fiscalizables por la SISS (manuales Continuidad Agua, Continuidad Alcantarillado y Presiones, 3ra ed. ago/2025). Plazo de aviso a SISS para fuerza mayor / caso fortuito: 24 horas."
      />

      <div className="border-b border-neutral-200">
        <div role="tablist" aria-label="Secciones de continuidad" className="-mb-px flex flex-wrap gap-2">
          {TABS.map((t) => {
            const active = t.id === tab;
            const Icon = t.icon;
            return (
              <button
                key={t.id}
                type="button"
                role="tab"
                id={`continuidad-tab-${t.id}`}
                aria-selected={active}
                aria-controls="continuidad-panel"
                onClick={() => elegir(t.id)}
                className={twMerge(
                  "inline-flex items-center gap-2 border-b-2 px-3 py-2 text-sm font-medium transition-colors",
                  "focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500",
                  active
                    ? "border-brand-600 text-brand-700"
                    : "border-transparent text-neutral-600 hover:border-neutral-300 hover:text-neutral-900",
                )}
              >
                <Icon className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" />
                {t.label}
              </button>
            );
          })}
        </div>
      </div>

      <div
        id="continuidad-panel"
        role="tabpanel"
        aria-labelledby={`continuidad-tab-${tab}`}
      >
        {tab === "agua" && <InterrupcionesPanel />}
        {tab === "alcantarillado" && <ObstruccionesPanel />}
        {tab === "presiones" && <PresionesPanel />}
        {tab === "plantas" && <PlantasPanel />}
      </div>
    </div>
  );
}
